// src/pages/Employment/CreateEmployment.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  FaArrowLeft,
  FaBuilding,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaBriefcase,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaCalendarAlt,
  FaCheckCircle,
  FaSpinner,
  FaPlus,
  FaTimes,
} from 'react-icons/fa';
import { successToast, errorToast } from '../../Utils/toast';
import { useCreateJobMutation } from '../../redux/services/employmentService/employment.Services';

function CreateEmployment() {
  const navigate = useNavigate();
  const [createJob, { isLoading }] = useCreateJobMutation();
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'job',
    type: 'full_time',
    description: '',
    requirements: '',
    responsibilities: '',
    location: '',
    salary_range: '',
    experience_required: '',
    qualification: '',
    company_name: '',
    company_website: '',
    company_email: '',
    contact_person: '',
    contact_phone: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    application_deadline: '',
    is_featured: false,
    is_verified: false,
    status: 'pending',
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setPreviewLogo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      errorToast('Job title is required');
      return;
    }

    if (!formData.description.trim()) {
      errorToast('Description is required');
      return;
    }

    if (!formData.company_name.trim()) {
      errorToast('Company name is required');
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });
      if (logoFile) {
        formDataToSend.append('logo', logoFile);
      }

      const response = await createJob(formDataToSend).unwrap();
      successToast(response?.message || 'Job created successfully');
      navigate('/employment');
    } catch (error) {
      console.error('Create job error:', error);
      errorToast(error?.data?.message || 'Failed to create job');
    }
  };

  return (
    <div>
      {/* HEADER */}
      <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              <Link to="/employment" className="text-primary me-2">
                <FaArrowLeft />
              </Link>
              Create Employment / Business
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/employment">Employment</Link>
              </li>
              <li className="breadcrumb-item">Create</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/employment')}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FaSpinner className="spinning me-2" />
                Creating...
              </>
            ) : (
              <>
                <FaPlus className="me-1" />
                Create Job
              </>
            )}
          </button>
        </div>
      </div>

      {/* FORM */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Basic Information */}
              <div className="col-12">
                <h5 className="fw-bold mb-3">Basic Information</h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>
                    Job Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter job title"
                    required
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="job">Job</option>
                    <option value="business">Business</option>
                    <option value="internship">Internship</option>
                    <option value="freelance">Freelance</option>
                    <option value="consultancy">Consultancy</option>
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group mb-3">
                  <label>
                    Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
              </div>

              <div className="col-12">
                <div className="form-group mb-3">
                  <label>
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter job description"
                    rows="4"
                    required
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Requirements</label>
                  <textarea
                    className="form-control"
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    placeholder="Enter job requirements"
                    rows="3"
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Responsibilities</label>
                  <textarea
                    className="form-control"
                    name="responsibilities"
                    value={formData.responsibilities}
                    onChange={handleChange}
                    placeholder="Enter job responsibilities"
                    rows="3"
                  />
                </div>
              </div>

              {/* Company Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaBuilding className="me-2" /> Company Information
                </h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Enter company name"
                    required
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Company Website</label>
                  <input
                    type="url"
                    className="form-control"
                    name="company_website"
                    value={formData.company_website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Company Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="company_email"
                    value={formData.company_email}
                    onChange={handleChange}
                    placeholder="company@example.com"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    className="form-control"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    placeholder="Contact person name"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="Contact phone number"
                  />
                </div>
              </div>

              {/* Location Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaMapMarkerAlt className="me-2" /> Location Information
                </h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Location</label>
                  <input
                    type="text"
                    className="form-control"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g., Mumbai, India"
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Salary Range</label>
                  <input
                    type="text"
                    className="form-control"
                    name="salary_range"
                    value={formData.salary_range}
                    onChange={handleChange}
                    placeholder="e.g., ₹5,00,000 - ₹8,00,000"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Experience Required</label>
                  <input
                    type="text"
                    className="form-control"
                    name="experience_required"
                    value={formData.experience_required}
                    onChange={handleChange}
                    placeholder="e.g., 2-5 years"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Qualification</label>
                  <input
                    type="text"
                    className="form-control"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    placeholder="e.g., B.Tech, MBA"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Application Deadline</label>
                  <input
                    type="date"
                    className="form-control"
                    name="application_deadline"
                    value={formData.application_deadline}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Address</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Full address"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>City</label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>State</label>
                  <input
                    type="text"
                    className="form-control"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Country</label>
                  <input
                    type="text"
                    className="form-control"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Pincode</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="Pincode"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaEnvelope className="me-2" /> Contact Information
                </h5>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@example.com"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone number"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>WhatsApp</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="WhatsApp number"
                  />
                </div>
              </div>

              {/* Logo Upload */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">
                  <FaGlobe className="me-2" /> Company Logo
                </h5>
              </div>

              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label>Upload Logo</label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                  <small className="text-muted">Max size: 2MB. Allowed: JPG, PNG, WEBP</small>
                </div>
              </div>

              {previewLogo && (
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>Logo Preview</label>
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={previewLogo}
                        alt="Logo Preview"
                        style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                        className="img-thumbnail"
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={removeLogo}
                      >
                        <FaTimes /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="col-12 mt-3">
                <h5 className="fw-bold mb-3">Options</h5>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label>Status</label>
                  <select
                    className="form-control"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                      id="is_featured"
                    />
                    <label className="form-check-label" htmlFor="is_featured">
                      <FaCheckCircle className="text-warning me-1" />
                      Featured Job
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group mb-3">
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="is_verified"
                      checked={formData.is_verified}
                      onChange={handleChange}
                      id="is_verified"
                    />
                    <label className="form-check-label" htmlFor="is_verified">
                      <FaCheckCircle className="text-success me-1" />
                      Verified Job
                    </label>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="col-12 mt-4">
                <div className="form-group">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <FaSpinner className="spinning me-2" />
                        Creating Job...
                      </>
                    ) : (
                      <>
                        <FaPlus className="me-2" />
                        Create Job
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateEmployment;